'use strict';

const ALL_ORGS_LOCAL_STORAGE_KEY = 'Organizations-All';
const CURRENT_ORG_LOCAL_STORAGE_KEY = 'Organizations-Current';
const SELECTED_ORG_LOCAL_STORAGE_KEY = 'Selected-Org';

export default angular
  .module('main.organizations.edit.dataProvider', [])
  .factory('EditOrganizationDataProvider', function(
    $q,
    $log,
    $rootScope,
    $state,
    $filter,
    utils,
    UsersService,
    Resource,
    ResourceGroup,
    OrganizationService,
    LocationsService,
    PermissionsService,
    BotsService,
    DevicesService,
    TagsService,
    ReportsService,
    BillingService,
  ) {
    const orderBy = $filter('orderBy');

    class EditOrganizationDataProvider {

      // #region Resources

      /**
       * Available organizations resource
       * @type {Resource}
       */
      availableOrganizationsResource = new Resource(
        () => {
          // TODO move get all orgs stuff to the OrganizationService
          return utils.getStorageItem(ALL_ORGS_LOCAL_STORAGE_KEY) || [];
        }
      );

      /**
       * Current organization resource
       * @type {Resource}
       */
      currentOrganizationResource = new Resource(
        () => {
          // TODO move get/set current org stuff to the OrganizationService
          return utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
        }
      );

      /**
       * Current user (admin currently logged in)
       * @type {Resource}
       */
      currentUserResource = new Resource(
        () => {
          return UsersService.getCurrentUserInfo();
        }
      );

      /**
       * Current organization ID
       * @type {Resource}
       */
      organizationIdResource = new Resource(
        () => {
          // TODO REMOVE LOCAL STORAGE THING
          return $state.params.organizationId || utils.getStorageItem(SELECTED_ORG_LOCAL_STORAGE_KEY)?.id;
        }
      );

      /**
       * Perent organization resource
       * @type {Resource}
       */
      parentOrganizationResource = new Resource(
        () => {
          let parentId = this.currentOrganizationResource.value.parentId;
          if (parentId) {
            let parentOrganization = this.availableOrganizationsResource.value.find(el => el.id === parentId);
            return parentOrganization;
          }
          return;
        },
        [
          this.currentOrganizationResource,
          this.availableOrganizationsResource,
        ]
      );

      /**
       * Test of current organization is top-level
       * @type {Resource}
       */
      isParentLevelOrganizationResource = new Resource(
        () => {
          let currentOrganization = this.currentOrganizationResource.value;
          return !currentOrganization.parentId;
        },
        [
          this.currentOrganizationResource,
        ]
      );

      /**
       * Organization resource
       * @type {Resource}
       */
      organizationResource = new Resource(() => {
        return OrganizationService.getOrganization(this.organizationIdResource.value)
          .then(data => {
            let organization = data.organizations ? data.organizations[0] : undefined;
            if (!organization) {
              return $q.reject({ resultCode: 8 });
            }
            utils.setStorageItem(SELECTED_ORG_LOCAL_STORAGE_KEY, organization);

            // Update current organization
            let currentOrg = utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
            if (currentOrg && currentOrg.id === organization.id) {
              utils.setStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY, organization);
            }

            // Force update current organization
            this.organizationIdResource.value = organization.id || this.currentOrganizationResource.value.id;
            return organization;
          });
      },
      [
        this.currentOrganizationResource,
        this.organizationIdResource,
      ],
        $state.params.organization || utils.getStorageItem(SELECTED_ORG_LOCAL_STORAGE_KEY)
      );

      /**
       * Organization properties resource
       * @type {Resource}
       */
      organizationPropsResource = new Resource(() => {
        return OrganizationService.getObjectsAndProperties(this.organizationIdResource.value)
          .then(data => {
            let properties = data.organizationObjects || [];
            return properties;
          });
      },
      [
        this.organizationIdResource,
      ]);

      /**
       * Organization administrators resource
       * @type {Resource}
       */
      organizationAdminsResource = new Resource(() => {
        const sortBy = 'firstName';
        const sortOrder = 'asc';
        return OrganizationService.getOrganizationAdmins(this.organizationIdResource.value, sortBy, sortOrder)
          .then(data => data.users || []);
      },
      [
        this.organizationIdResource,
      ]);

      /**
       * Organization bots resource
       * @type {Resource}
       */
      organizationBotsResource = new Resource(() => {
        return BotsService.getOrganizationBots(this.organizationIdResource.value)
          .then(data => data.bots || []);
      }, [
        this.organizationIdResource,
      ]);

      /**
       * Administrative roles resource
       * @type {Resource}
       */
      adminRolesResource = new Resource(() => {
        return OrganizationService.getRoles()
          .then(data => data.roles || []);
      }, []);

      /**
       * List of countries available for selection
       * @type {Resource}
       */
      countriesResource = new Resource(() => {
        return LocationsService.getCountries()
          .then((data) => data.countries || []);
      }, []);

      /**
       * Resource for checking if current user is allowed to edit current organization
       * @type {Resource}
       */
      canIEditOrganizationResource = new Resource(
        () => {
          return PermissionsService.canIEditOrganization(this.organizationResource.value);
        },
        [
          this.organizationResource
        ]
      );

      /**
       * Resource for checking if current user is allowed to edit current organization
       * @type {Resource}
       */
      canIEditAdvancedSettings = new Resource(
        () => {
          return PermissionsService.canIEditOrganizationSettings();
      });

      /**
       * Device types resource
       * @type {Resource}
       */
      deviceTypesResource = new Resource(() => {
        return DevicesService.getDeviceTypes().then(data => data?.deviceTypes || []);
      });

      /**
       * Popular tags resource
       * @type {Resource}
       */
      popularTagsResource = new Resource(() => {
        return $q.all([
          TagsService.getPopularTags(this.organizationIdResource.value, 1),
          TagsService.getPopularTags(this.organizationIdResource.value, 2)
        ])
        .then(data => {
          let result = {
            user: data[0].tags || [],
            location: data[1].tags || [],
          };
          return result;
        });
      },
      [
        this.organizationIdResource,
      ]);

      /**
       * Billing items resource
       * @type {Resource}
       */
      billingItemsResource = new Resource(() => {
        return BillingService.getBillingItems().then(data => data?.items || []);
      });

      /**
       * Billing plans resource
       * @type {Resource}
       */
      billingPlansResource = new Resource(() => {
        return BillingService.getBillingPlans().then(data => data?.plans || []);
      });

      /**
       * Oranization billing plans resource
       * @type {Resource}
       */
      organizationBillingPlansResource = new Resource(() => {
        return BillingService.getOrganizationBillingPlans(this.organizationIdResource.value)
          .then(data => data?.plans || []);
      },
      [
        this.organizationIdResource,
      ]);

      /**
       * Brands resource
       * @type {Resource}
       */
      brandsResource = new Resource(() => {
        return OrganizationService.getBrands()
          .then(data => data?.brands || []);
      });

      /**
       * Organization timezone ID
       * @type {Resource}
       */
      organizationTimeZoneIdResource = new Resource(() => {
        if (this.organizationResource.value.timezone
          && this.organizationResource.value.timezone.id
          && !!moment.tz.zone(this.organizationResource.value.timezone.id)) {
          return this.organizationResource.value.timezone.id;
        }
        return moment.tz.guess() || 'GMT';
      }, [
        this.organizationResource
      ]);

      /**
       * Resources that we need for every tab
       * @type {ResourceGroup}
       */
      mainResourceGroup = new ResourceGroup([
        this.currentOrganizationResource,
        this.currentUserResource
      ]);

      // #endregion

      /**
       * Current (active) tab
       * @type {'contacts'|'info'|'settings'|'admins'|'reports'|'appearance'|'templates'|'bots'}
       */
      get activeTab() {
        return this.#_activeTab;
      }

      /**
       * Previous tab
       * @return {'contacts'|'info'|'settings'|'admins'|'reports'|'appearance'|'templates'|'bots'|undefined}
       */
      get previousTab() {
        return this.#_previousTab;
      }

      set activeTab(val) {
        this.#_visitedTabs[val] = true;
        if (this.#_activeTab !== val) {
          this.#_previousTab = this.#_activeTab;
        }
        this.#_activeTab = val;
      }

      /**
       * Hash of visited tabs
       */
      get visitedTabs() {
        return this.#_visitedTabs;
      }

      // #region Private fields

      #_activeTab;

      #_previousTab;

      #_visitedTabs = {};

      // #endregion

      constructor() {
      }

      // #region Public methods

      /**
       * Universal handle function for Cancel or Back button
       */
      goBack() {
        $state.go('main.organizations.list');
      }

      /**
       * Destroy current Data Provider.
       * Obviously, you can not use this object after destruction.
       */
      destroy() {
        // destroy every Resource and Resource Group in current object
        for (let [key, value] of Object.entries(this)) {
          if (value && value.isResource) {
            value.destroy();
            delete this[key];
          }
        }
      }

      /**
       * Save organization information
       * @param {*} orgInfo
       * @returns {Promise}
       */
      saveOrganizationInfo(orgInfo) {
        return OrganizationService.updateOrganization(this.organizationIdResource.value, orgInfo)
          .then(() => {
            return $q.when(this.organizationResource.init(true));
          });
      }

      /**
       * Update organization objects and properties
       * @param {*} orgProps
       * @returns {Promise}
       */
      saveOrganizationProps(orgProps) {
        return OrganizationService.updateObjectsAndProperties(this.organizationIdResource.value, orgProps)
          .then(() => {
            return $q.when(this.organizationPropsResource.init(true));
          });
      }

      /**
       * Upload organization large object
       * @param {*} fileContent File content
       * @param {string} propName Object name
       * @param {string} fileType File type
       * @returns {Promise}
       */
      uploadOrganizationObject(fileContent, propName, fileType) {
        let type = fileType || 'image/png';
        return OrganizationService.uploadLargeObject(this.organizationIdResource.value, fileContent, propName, false, type)
          .then(() => {
            // Clear cache
            OrganizationService.getObjectsAndProperties.clearCache(this.organizationIdResource.value);
            this.organizationPropsResource.dropCacheRecursively();

            return $q.when(this.organizationPropsResource.init(true));
          });
      }

      /**
       * Delete organization object or property
       * @param {string} propName Property or object name
       * @returns {Promise}
       */
      deleteOrganizationObject(propName) {
        return OrganizationService.deleteObjectOrProperty(this.organizationIdResource.value, propName)
          .then(() => {

            // Clear cache
            OrganizationService.getObjectsAndProperties.clearCache(this.organizationIdResource.value);
            this.organizationPropsResource.dropCacheRecursively();

            return $q.when(this.organizationPropsResource.init(true));
          });
      }

      /**
       * Grant admin role to specified user
       * @param {number} adminId User ID to grant role.
       * @param {number} roleId Role ID.
       * @returns {Promise}
       */
      grantAdminRole(adminId, roleId) {
        return OrganizationService.grantRoleToUser(adminId, roleId)
          .then(() => {
            return $q.when(this.organizationAdminsResource.init(true));
          });
      }

      /**
       * Add user to organization admins
       * @param {number} userId User ID to add as admin.
       * @returns {Promise}
       */
      addUserToOrganizationAdmins(userId) {
        return OrganizationService.addUserToOrgAdmins(this.organizationIdResource.value, userId)
        .then(() => {
          return $q.when(this.organizationAdminsResource.init(true));
        });
      }

      /**
       * Search users across entire cloud
       * @param {string} [email] Email address to search by.
       * @param {string} [phone] Phone number to search by.
       * @returns {Promise}
       */
      searchUsersGlobal(email, phone) {
        return UsersService.searchUsers(email, phone)
          .then(data => data);
      }

      /**
       * Delete specific user from organization admins.
       * @param {number} userId User ID to remove.
       * @returns {Promise}
       */
      removeAdminFromOrganization(userId) {
        return OrganizationService.removeUserFromOrgAdmins(this.organizationIdResource.value, userId)
          .then(() => {
            return $q.when(this.organizationAdminsResource.init(true));
          });
      }

      /**
       * Revoke admon role from specified user.
       * @param {number} userId User ID to remove.
       * @returns {Promise}
       */
      revokeAdminRole(userId) {
        return OrganizationService.revokeAdminRole(userId);
      }

      /**
       * Organization report groups resource
       * @type {Resource}
       */
      organizationReportGroupsResource = new Resource(() => {
        return ReportsService.getReportGroups(this.organizationIdResource.value)
          .then(data => data.groups || []);
      },
      [
        this.organizationIdResource,
      ]);

      /**
       * Set organization report group status
       * @param {number} groupId Report group ID
       * @param {boolean} activate Activation flag
       * @param {number} [notificationGroupId] Notification group.
       * @returns {Promise}
       */
      setReportGroupStatus(groupId, activate, notificationGroupId) {
        return ReportsService.setReportGroupStatus(this.organizationIdResource.value, groupId, activate, notificationGroupId)
          .then(() => {
            return $q.when(this.organizationReportGroupsResource.init(true));
          });
      }

      /**
       * Search for organization bots
       * @returns {Promise}
       */
      searchOrganizationBots() {
        return BotsService.searchOrganizationBots(this.organizationIdResource.value, {
          rowCount: 1,
          firstRow: 0
        }).then(data => data.apps || []);
      }

      /**
       * Set organization bot status
       * @param {string} appInstanceId ID of the specific bot
       * @param {number} status Bot status on/off
       * @returns {Promise}
       */
      setOrganizationBotStatus(appInstanceId, status) {
        return BotsService.setBotStatus(appInstanceId, status)
          .then(() => {
            return $q.when(this.organizationBotsResource.init(true));
          });
      }

      /**
       * Delete specified bot instance
       * @param {appIntanceId} appInstanceId ID of the specific bot
       * @returns {Promise}
       */
      deleteOrganizationBot(appInstanceId) {
        return BotsService.deleteBotInstance(appInstanceId)
          .then(() => {
            return $q.when(this.organizationBotsResource.init(true));
          });
      }

      /**
       * Create or update billing plan
       * @param billingPlan
       * @param {number} [planId]
       * @returns {Promise}
       */
      setBillingPlan(billingPlan, planId) {
        return BillingService.setBillingPlan(billingPlan, planId)
          .then(() => {
            return $q.when(this.billingPlansResource.init(true));
          });
      }

      /**
       * Get billing plan versions
       * @param {number} planId
       * @returns {Promise}
       */
      getBillingPlanVersions(planId) {
        return BillingService.getBillingPlanVersions(planId)
          .then(data => data?.versions || []);
      }

      /**
       * Create or update billing plan version
       * @param {number} planId
       * @param billingPlanVersion
       * @param {number} [planVersionId]
       * @returns
       */
      setBillingPlanVersion(planId, billingPlanVersion, planVersionId) {
        return BillingService.setBillingPlanVersion(planId, billingPlanVersion, planVersionId)
          .then(data => data);
      }

      /**
       * Create or update organization billing plan
       * @param {number} planId
       * @param {string|number} startDate
       * @returns {Promise}
       */
      setOrganizationBillingPlan(planId, startDate) {
        return BillingService.setOrganizationBillingPlan(this.organizationIdResource.value, planId, startDate)
          .then(() => {
            return $q.when(this.n.init(true));
          });
      }

      /**
       * Delete organization billing plan
       * @param {string|number} startDate
       * @returns {Promise}
       */
      deleteOrganizationBillingPlan(startDate) {
        return BillingService.deleteOrganizationBillingPlan(this.organizationIdResource.value, startDate)
          .then(() => {
            return $q.when(this.organizationBillingPlansResource.init(true));
          });
      }

      /**
       * Search organization bills by date
       * @param {string|number} startDate
       * @param {string|number} [endDate]
       * @returns {Promise}
       */
      searchOrganizationBills(startDate, endDate) {
        return BillingService.getBills(this.organizationIdResource.value, startDate, endDate)
          .then(data => data.bills || []);
      }

      /**
       * Get specified bill content
       * @param {number} billId
       * @param {string} [format] Optional (default is 'pdf')
       * @returns {*}
       */
      getOrganizationBill(billId, format) {
        return BillingService.getBillContent(this.organizationIdResource.value, billId, format);
      }

      // #endregion

    }

    return EditOrganizationDataProvider;
  })
  .name;
